import React from 'react'

function PolicyAndPrivacy() {
  return (
    <div className="min-h-screen bg-white selection:bg-[#952877]/10 selection:text-[#952877]">

      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Decorative Blurs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute top-20 left-0 w-[300px] h-[300px] bg-[#592aa7]/5 blur-[100px] rounded-full pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">

          <h1 className="text-5xl md:text-8xl font-black text-[#17091a] mb-6 tracking-tighter leading-none">
            Privacy Policy
          </h1>
          <p className="text-xl md:text-2xl text-gray-500 max-w-3xl mx-auto font-light leading-relaxed">
            Your privacy and data security are our top priorities
          </p>
        </div>
      </section>

      {/* --- INTRODUCTION --- */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-6">
            <h2 className="text-xs font-black uppercase tracking-[0.3em] text-[#952877]">Introduction</h2>
            <p className="text-lg text-gray-600 leading-[1.8] font-medium">
              At viconAI, we are committed to protecting your privacy and handling your personal 
              information with care and transparency. This Privacy Policy explains how we collect, 
              use, store, and protect your data when you visit{' '}
              <a href="https://viconai.com/" target="_blank" rel="noopener noreferrer" className="text-[#952877] hover:underline font-bold">
                https://viconai.com/
              </a>.
            </p>
            <p className="text-lg text-gray-600 leading-[1.8] font-medium">
              We adhere to GDPR principles including lawfulness, fairness, transparency, purpose 
              limitation, data minimization, accuracy, storage limitation, and integrity in all our 
              data handling practices.
            </p>
          </div>
        </div>
      </section>

      {/* --- PERSONAL INFORMATION --- */}
      <section className="py-20 bg-[#fafafa]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-black text-[#17091a] mb-6 tracking-tight">
            What We Consider Personal Information
          </h2>
          <p className="text-lg text-gray-600 leading-[1.8] font-medium">
            Personal information refers to any data that can identify you as an individual, 
            including but not limited to your name, email address, job title, company name, 
            and contact details.
          </p>
        </div>
      </section>

      {/* --- INFORMATION COLLECTION --- */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-black text-[#17091a] mb-12 tracking-tight">
            Information Collection and Usage
          </h2>

          <div className="space-y-12">
            {/* Browsing */}
            <div>
              <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                Browsing Our Website
              </h3>
              <p className="text-lg text-gray-600 leading-[1.8] font-medium">
                You can explore most areas of our website without providing personal information. 
                However, certain features and resources require basic information to function properly.
              </p>
            </div>

            {/* How We Collect */}
            <div>
              <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                How We Collect Data
              </h3>
              <p className="text-lg text-gray-600 leading-[1.8] font-medium mb-4">
                We collect information through:
              </p>
              <div className="space-y-3">
                {[
                  'Form submissions on our website',
                  'Cookies and similar tracking technologies',
                  'User interactions with our platform',
                  'Communication channels such as email and contact forms'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#952877] mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Purpose */}
            <div>
              <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                Purpose of Data Collection
              </h3>
              <p className="text-lg text-gray-600 leading-[1.8] font-medium mb-4">
                We use your information to:
              </p>
              <div className="space-y-3">
                {[
                  'Analyze website traffic and user behavior patterns',
                  'Enhance website security and prevent unauthorized access',
                  'Track visit sources and session timestamps',
                  'Enable social media integration and sharing features',
                  'Provide access to restricted content and premium features',
                  'Respond to inquiries, support requests, and feedback',
                  'Improve website performance and user experience',
                  'Deliver personalized content and recommendations'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#952877] mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- DATA SHARING GLASS CARD --- */}
      <section className="py-24 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-black text-[#17091a] mb-12 tracking-tight text-center">
            Data Sharing and Disclosure
          </h2>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Commitment Card */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#952877]/20 to-[#592aa7]/20 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition duration-700"></div>
              <div className="relative bg-white/40 backdrop-blur-xl border border-white/60 rounded-[3rem] p-10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)]">
                <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                  Our Commitment to Privacy
                </h3>
                <p className="text-gray-600 leading-[1.8] font-medium">
                  viconAI does not sell, rent, or share your personal information with unaffiliated 
                  third parties for marketing purposes. Your data remains confidential and is handled 
                  with strict security protocols.
                </p>
              </div>
            </div>

            {/* Limited Sharing Card */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#592aa7]/20 to-[#952877]/20 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition duration-700"></div>
              <div className="relative bg-white/40 backdrop-blur-xl border border-white/60 rounded-[3rem] p-10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)]">
                <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                  Limited Data Sharing
                </h3>
                <p className="text-gray-600 leading-[1.8] font-medium mb-4">
                  We may share your information only in the following circumstances:
                </p>
                <div className="space-y-3">
                  {[
                    'With authorized employees and team members who require access to perform their duties',
                    'With professional advisers bound by confidentiality agreements',
                    'With trusted service providers who assist in website operations under strict confidentiality terms',
                    'When legally required by court order, regulation, or governmental authority',
                    'To protect our rights, property, or safety, or that of our users'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#952877] mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-600 font-medium leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- COOKIE POLICY --- */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-black text-[#17091a] mb-12 tracking-tight">
            Cookie Policy
          </h2>

          <div className="space-y-12">
            <div>
              <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                What Are Cookies
              </h3>
              <p className="text-lg text-gray-600 leading-[1.8] font-medium">
                Cookies are small text files stored on your device that help websites remember your 
                preferences and improve your browsing experience.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                How We Use Cookies
              </h3>
              <p className="text-lg text-gray-600 leading-[1.8] font-medium mb-4">
                Our website uses cookies to:
              </p>
              <div className="space-y-3">
                {[
                  'Remember your preferences and settings',
                  'Analyze traffic patterns and user engagement',
                  'Enable core website functionality',
                  'Provide social media features',
                  'Optimize content delivery'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#952877] mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
                Managing Cookie Preferences
              </h3>
              <p className="text-lg text-gray-600 leading-[1.8] font-medium mb-4">
                You have full control over cookie settings. Most browsers allow you to:
              </p>
              <div className="space-y-3 mb-6">
                {[
                  'Block all cookies',
                  'Accept cookies selectively',
                  'Delete existing cookies',
                  'Receive notifications when cookies are sent'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#952877] mt-3 flex-shrink-0"></div>
                    <span className="text-lg text-gray-600 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-600 leading-[1.8] font-medium">
                Please note that blocking essential cookies may limit your access to certain website 
                features and affect your overall experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- DATA SECURITY CARDS --- */}
      <section className="py-24 bg-[#fafafa]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-black text-[#17091a] mb-12 tracking-tight">
            Data Security
          </h2>

          <div className="bg-white rounded-[2.5rem] p-10 border border-gray-100 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)]">
            <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">
              Our Security Measures
            </h3>
            <p className="text-lg text-gray-600 leading-[1.8] font-medium mb-6">
              We implement industry-standard security protocols to protect your personal information, 
              including:
            </p>
            <div className="space-y-3 mb-6">
              {[
                'Encrypted data transmission and storage',
                'Regular security audits and updates',
                'Access controls and authentication systems',
                'Secure server infrastructure',
                'Employee training on data protection practices'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#952877]/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-[#952877]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-lg text-gray-600 font-medium">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-600 leading-[1.8] font-medium">
              While we take extensive measures to safeguard your data, please understand that no 
              method of electronic transmission or storage is completely secure.
            </p>
          </div>
        </div>
      </section>

      {/* --- YOUR RIGHTS --- */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-black text-[#17091a] mb-12 tracking-tight">
            Your Rights
          </h2>

          <p className="text-lg text-gray-600 leading-[1.8] font-medium mb-6">
            You have the right to:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {[
              'Access your personal data we hold',
              'Request corrections to inaccurate information',
              'Request deletion of your data',
              'Withdraw consent for data processing',
              'Object to certain data processing activities',
              'Request data portability'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-4 bg-[#fafafa] p-6 rounded-2xl border border-gray-100">
                <div className="w-8 h-8 rounded-full bg-[#952877]/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-[#952877]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm font-bold text-[#17091a]/80 uppercase tracking-tight leading-snug">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-lg text-gray-600 leading-[1.8] font-medium">
            To exercise these rights, please contact us at{' '}
            <a href="mailto:contact@viconai.com" className="text-[#952877] hover:underline font-bold">
              contact@viconai.com
            </a>.
          </p>
        </div>
      </section>

      {/* --- CHANGES TO POLICY --- */}
      <section className="py-20 bg-[#fafafa]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-black text-[#17091a] mb-6 tracking-tight">
            Changes to This Policy
          </h2>
          <p className="text-lg text-gray-600 leading-[1.8] font-medium">
            viconAI reserves the right to update this Privacy Policy periodically. We will notify 
            users of significant changes through our website or via email. Continued use of our 
            services after changes constitutes acceptance of the updated policy.
          </p>
        </div>
      </section>

      {/* --- CONTACT SECTION (DARK) --- */}
      <section className="py-24 bg-[#17091a] rounded-[4rem] text-white  m-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#952877]/10 blur-[150px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter text-center">
            Contact Us
          </h2>
          <p className="text-xl text-gray-300 text-center mb-12 font-light">
            For questions, concerns, or requests regarding this Privacy Policy or your personal 
            data, please reach out to us at:
          </p>

          <div className="bg-white/5 backdrop-blur-md p-10 rounded-3xl border mb-10 border-white/10 max-w-2xl mx-auto">
            <div className="text-center space-y-4">
              <p className="text-2xl font-black text-white">viconAI</p>
              <p className="text-gray-300">
                Email:{' '}
                <a href="mailto:contact@viconai.com" className="text-[#952877] hover:underline font-bold">
                  contact@viconai.com
                </a>
              </p>

            </div>
          </div>
        </div>
      </section>


    </div>
  )
}

export default PolicyAndPrivacy